/**
 * web-sign-in-checkout D5, D7 — the booking flow on the website session.
 *
 * The website's counterparts of the Mini App's flow hooks: the same
 * `/api/webapp` flow routes, called on the website cookie through `webFetch`
 * instead of the app's global token. The flow is named by its `flowToken`, which
 * the checkout keeps in its address (`/checkout?flow=<token>`).
 */
import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CompleteStepResponse, PlaceholderResponse, WebAppFlowContextResponse } from '@/api/types';

import { CLIENT_SCOPE, SiteFetchError, useWebSession, webFetch } from '../api/session';
import { accountKeys } from './useWebAccount';

export const flowKeys = {
  context: (flowToken: string) => [...CLIENT_SCOPE, 'flow', flowToken] as const,
};

/** The flow as the server holds it: its step, its held time, its offering. */
export function useWebFlowContext(flowToken: string | null) {
  const { status } = useWebSession();
  return useQuery({
    queryKey: flowKeys.context(flowToken ?? ''),
    queryFn: () => webFetch<WebAppFlowContextResponse>(`/flow/${encodeURIComponent(flowToken!)}`),
    enabled: Boolean(flowToken) && status === 'signedIn',
    retry: false,
  });
}

/**
 * service-by-phone D5, D6: the checkout's Save on the call line. Writes the number alone through the
 * phone door, then reads the flow again (the line names the saved number) and marks Profile stale.
 */
export function useWebSavePhoneToCall(flowToken: string | null) {
  const queryClient = useQueryClient();
  return useCallback(
    async (phone: string): Promise<void> => {
      await webFetch<{ phone: string }>('/profile/phone', { method: 'PUT', body: JSON.stringify({ phone }) });
      await Promise.all([
        flowToken ? queryClient.invalidateQueries({ queryKey: flowKeys.context(flowToken) }) : undefined,
        queryClient.invalidateQueries({ queryKey: accountKeys.profile }),
      ]);
    },
    [flowToken, queryClient],
  );
}

/** The browser's zone, recorded on the hold as the app's hold records it. */
function browserZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

const post = <T>(path: string, body: unknown, headers?: Record<string, string>) =>
  webFetch<T>(path, { method: 'POST', body: JSON.stringify(body), headers });

/** The website's flow calls, as `ServiceDetailPage`'s commit and the event Reserve make them. */
export const webFlow = {
  createServiceFlow: (serviceId: string) => post<{ flowToken: string }>('/hub/flow', { serviceId }),
  /** The practitioner whose open times the website showed (the hold refuses a flow without one). */
  servicePractitioner: async (serviceId: string) =>
    (await webFetch<{ service: { provider: { practitionerProfileId: string } } }>(`/hub/services/${encodeURIComponent(serviceId)}`))
      .service.provider.practitionerProfileId,
  completeHub: (flowToken: string, selectedServiceId: string, selectedPractitionerId: string) =>
    post<CompleteStepResponse>(`/flow/${encodeURIComponent(flowToken)}/complete-step`, {
      step: 'HUB',
      data: { selectedServiceId, selectedPractitionerId },
    }),
  hold: (flowToken: string, slotStartsAt: string, idempotencyKey: string) =>
    post<PlaceholderResponse>('/placeholder', { flowToken, slotStartsAt, timezone: browserZone() }, { 'Idempotency-Key': idempotencyKey }),
  completeCalendar: (flowToken: string, selectedSlot: string) =>
    post<CompleteStepResponse>(`/flow/${encodeURIComponent(flowToken)}/complete-step`, { step: 'CALENDAR', data: { selectedSlot } }),
  createEventFlow: (eventId: string, idempotencyKey: string) =>
    post<{ flowToken: string }>(`/hub/events/${encodeURIComponent(eventId)}/flow`, { timezone: browserZone() }, { 'Idempotency-Key': idempotencyKey }),
  /** shared-client-screens D5: a series' spot, held as the app's Book This Series holds it. */
  createSeriesFlow: (seriesId: string, idempotencyKey: string) =>
    post<{ flowToken: string }>(`/hub/series/${encodeURIComponent(seriesId)}/flow`, { timezone: browserZone() }, { 'Idempotency-Key': idempotencyKey }),
  cancel: (flowToken: string) => post<unknown>(`/flow/${encodeURIComponent(flowToken)}/cancel`, {}),
  /**
   * Steps a service flow back to its time, as the app's Back does from the forms or the payment: the step back onto
   * the time lets the held time go (or answers PAYMENT_COMPLETED_CONFLICT when its payment already went through).
   * True once the flow is at its time, false when it cannot get there.
   */
  backToTime: async (flowToken: string): Promise<boolean> => {
    for (let i = 0; i < 6; i++) {
      const res = await post<{ currentStep?: string; flowCancelled?: boolean }>(`/flow/${encodeURIComponent(flowToken)}/go-back`, {});
      if (res.flowCancelled) return false;
      if (res.currentStep === 'CALENDAR') return true;
    }
    return false;
  },
};

/** The open flow named by a `409 ACTIVE_FLOW_EXISTS`, or null for any other error. */
export function activeFlowOf(
  error: unknown,
): { flowToken: string; selectedServiceId?: string; selectedEventId?: string; selectedSeriesId?: string } | null {
  if (!(error instanceof SiteFetchError) || error.code !== 'ACTIVE_FLOW_EXISTS') return null;
  const { flowToken, selectedServiceId, selectedEventId, selectedSeriesId } = error.body as Record<string, unknown>;
  if (typeof flowToken !== 'string' || !flowToken) return null;
  return {
    flowToken,
    ...(typeof selectedServiceId === 'string' ? { selectedServiceId } : {}),
    ...(typeof selectedEventId === 'string' ? { selectedEventId } : {}),
    ...(typeof selectedSeriesId === 'string' ? { selectedSeriesId } : {}),
  };
}

// 409s about the studio, the offering or the flow, not the time: the server's message, the pick kept (as the app does).
const NOT_TIME_CONFLICTS = new Set(['BOOKING_PAUSED', 'SERVICE_INACTIVE', 'STEP_MISMATCH']);

/** True when the chosen time is gone (taken or no longer open). */
export function isTimeGone(error: unknown): boolean {
  return (
    error instanceof SiteFetchError && !NOT_TIME_CONFLICTS.has(error.code ?? '') && (error.status === 409 || error.status === 410)
  );
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
